import styles from './styles.module.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { trackShare } from '@disco/state-management/actions/TrackShareActions'
import { trackSharesWithContacts } from '@disco/state-management/selectors/shares'
import { getUser } from '@disco/state-management/selectors/user'

import type { Track } from 'types/track'

import { RecipientInput } from 'components/common/recipient-input'
import type { RecipientTag } from 'components/common/recipient-input'
import {
  Box,
  Button,
  Flex as DsFlex,
  Link,
  Spinner,
  Text,
  TextArea,
  TextField,
} from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { buildShareRecipients, isSameRecipient } from './share-recipients'

interface TrackEmailTabProps {
  track: Track
  onClose?: () => void
  onViewShares?: () => void
}

const MAX_RECIPIENTS_LENGTH = 200

/**
 * "Email track" panel of the share modal. Collects recipients (people or
 * whole DISCO businesses, via {@link RecipientInput}) and sends the track
 * through the `trackShare` action, enforcing the 200-recipient cap.
 */
export const TrackEmailTab: React.FC<TrackEmailTabProps> = ({
  track,
  onClose,
  onViewShares,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(getUser)
  const { sharesLoaded, sharesItems } = useSelector(state =>
    trackSharesWithContacts(state, { id: track.id }),
  )

  const defaultSubject = useMemo(() => {
    const title = track.name || 'untitled track'
    const businessName = user.business?.name
    const fromBusinessName =
      user.business?.hide_business_name || !businessName
        ? ''
        : `from ${businessName} `
    return `${user.first_name} ${user.last_name} ${fromBusinessName}has sent you ${title}`
  }, [track.name, user])

  const [recipients, setRecipients] = useState<RecipientTag[]>([])
  const [subject, setSubject] = useState(defaultSubject)
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const addRecipient = (
    email?: string,
    name?: string,
    contactId?: number,
    meta?: { type?: 'email' | 'user' | 'business' },
  ) => {
    const trimmedEmail = email?.trim() || ''
    setRecipients(prev => {
      const candidate = { contactId, type: meta?.type, email: trimmedEmail }
      const isDuplicate = prev.some(r => isSameRecipient(r, candidate))
      if (isDuplicate) return prev
      return [
        ...prev,
        {
          id: `${Date.now()}-${Math.random()}`,
          email: trimmedEmail || undefined,
          name: name?.trim() || undefined,
          contactId,
          isValid: true,
          type: meta?.type,
        },
      ]
    })
  }

  const removeRecipient = (id: string) => {
    setRecipients(prev => prev.filter(r => r.id !== id))
  }

  useEffect(() => {
    if (recipients.length > MAX_RECIPIENTS_LENGTH) {
      mixpanel.sendPureEvent(
        `User attempted to share with more than ${MAX_RECIPIENTS_LENGTH} recipients`,
        { recipient_count: recipients.length },
      )
    }
  }, [recipients.length])

  const isOverRecipientLimit = recipients.length > MAX_RECIPIENTS_LENGTH

  const canSend =
    recipients.length > 0 &&
    !isOverRecipientLimit &&
    subject.trim() &&
    !isLoading

  const handleSend = async () => {
    if (!canSend) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await dispatch(
        trackShare(track, buildShareRecipients(recipients) as any, {
          manual: false,
          subject: subject.trim(),
          message: message.trim(),
        }),
      )

      if (result?.id) {
        navigate(`/explorer/tracks/${track.id}/shared/${result.id}`)
      } else {
        onClose?.()
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'There was an error sharing this track. Please try again.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenStats = () => {
    navigate(`/explorer/tracks/${track.id}/stats`)
    dispatch(sendMixpanelEvent('Access track stats via share modal'))
  }

  const renderStatsCaption = () => {
    if (!sharesLoaded) {
      return (
        <Text size="2" color="gray">
          Loading…
        </Text>
      )
    }

    if (!sharesItems || sharesItems.length === 0) return null

    return (
      <Text size="2" color="gray">
        View current{' '}
        <Link
          href="#"
          onClick={e => {
            e.preventDefault()
            onViewShares?.()
          }}
        >
          Share URLs
        </Link>{' '}
        and{' '}
        <Link
          href="#"
          onClick={e => {
            e.preventDefault()
            handleOpenStats()
          }}
        >
          Access Stats
        </Link>
        .
      </Text>
    )
  }

  return (
    <>
      <Box mb="3">
        <RecipientInput
          recipients={recipients}
          onAdd={addRecipient}
          onRemove={removeRecipient}
          includeBusinesses
          includeBriefs={false}
          label="Enter email addresses or search for contacts, mailing lists, or businesses"
          aria-label="Track share recipients"
        />
      </Box>

      <Box className={styles.scrollArea}>
        <Box mb="3">
          <Text size="2" weight="medium" as="label" htmlFor="track-subject">
            Subject
          </Text>
          <TextField.Root
            id="track-subject"
            size="2"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Enter your email subject"
            mt="1"
          />
        </Box>

        <Box mb="3">
          <Text size="2" weight="medium" as="label" htmlFor="track-message">
            Message
          </Text>
          <TextArea
            id="track-message"
            size="2"
            placeholder="Message"
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={6}
            mt="1"
          />
        </Box>

        {isOverRecipientLimit && (
          <Box mb="3">
            <Text size="2" color="red">
              You&apos;re sharing with {recipients.length} recipients — please
              reduce your recipient list to under {MAX_RECIPIENTS_LENGTH}.
            </Text>
          </Box>
        )}

        {error && (
          <Box mb="3">
            <Text size="2" color="red">
              {error}
            </Text>
          </Box>
        )}
      </Box>

      <DsFlex justify="between" align="center">
        <Box>{renderStatsCaption()}</Box>
        <DsFlex gap="3">
          <Button variant="soft" color="gray" size="2" onClick={onClose}>
            Cancel
          </Button>
          {recipients.length > 0 && (
            <Button onClick={handleSend} disabled={!canSend}>
              {isLoading && <Spinner size="1" />}
              <span>
                {isLoading
                  ? 'Sending'
                  : `Send to ${recipients.length} recipient${
                      recipients.length === 1 ? '' : 's'
                    }`}
              </span>
            </Button>
          )}
        </DsFlex>
      </DsFlex>
    </>
  )
}
